/**
 * A chain storefront as it appears inside the marketplace chat.
 *
 * Searching a brand is a different intent from searching a product: the
 * shopper already knows who they're buying from, so the answer is that
 * merchant's menu organised the way the merchant organises it — by department
 * — rather than a ranked list of competing results.
 */

import { Dish, DishExtra, FoodListing } from './food'

const IMG = {
  whopper:
    'https://images.unsplash.com/photo-1571091718767-18b5b1457add?auto=format&fit=crop&q=80&w=800',
  whopperAlt:
    'https://images.unsplash.com/photo-1550547660-d9450f859349?auto=format&fit=crop&q=80&w=800',
  cheeseburger:
    'https://images.unsplash.com/photo-1586190848861-99aa4a171e90?auto=format&fit=crop&q=80&w=800',
  angus:
    'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&q=80&w=800',
  chicken:
    'https://images.unsplash.com/photo-1606755962773-d324e0a13086?auto=format&fit=crop&q=80&w=800',
  nuggets:
    'https://images.unsplash.com/photo-1562967914-608f82629710?auto=format&fit=crop&q=80&w=800',
  plant:
    'https://images.unsplash.com/photo-1520072959219-c595dc870360?auto=format&fit=crop&q=80&w=800',
  fries:
    'https://images.unsplash.com/photo-1573080496219-bb080dd4f877?auto=format&fit=crop&q=80&w=800',
  onionRings:
    'https://images.unsplash.com/photo-1639024471283-03518883512d?auto=format&fit=crop&q=80&w=800',
  drink:
    'https://images.unsplash.com/photo-1581006852262-e4307cf6283a?auto=format&fit=crop&q=80&w=800',
  sundae:
    'https://images.unsplash.com/photo-1563805042-7684c019e1cb?auto=format&fit=crop&q=80&w=800',
  shake:
    'https://images.unsplash.com/photo-1572490122747-3968b75cc699?auto=format&fit=crop&q=80&w=800',
  breakfast:
    'https://images.unsplash.com/photo-1550317138-10000687a72b?auto=format&fit=crop&q=80&w=800',
  kids: 'https://images.unsplash.com/photo-1594212699903-ec8a3eca50f5?auto=format&fit=crop&q=80&w=800',
  bundle:
    'https://images.unsplash.com/photo-1610614819513-58e34989848b?auto=format&fit=crop&q=80&w=800',
  storefront:
    'https://images.unsplash.com/photo-1552566626-52f8b828add9?auto=format&fit=crop&q=80&w=1200',
}

/** Recurring add-ons, written once. */
const MEAL_EXTRAS: DishExtra[] = [
  {
    id: 'bkx-fries',
    label: 'Medium fries',
    price: 9,
    group: 'Make it a meal',
  },
  {
    id: 'bkx-rings',
    label: 'Onion rings',
    price: 11,
    group: 'Make it a meal',
  },
  {
    id: 'bkx-drink',
    label: 'Medium soft drink',
    price: 8,
    group: 'Make it a meal',
  },
]

const SAUCES: DishExtra[] = [
  {
    id: 'bkx-bbq',
    label: 'Barbecue dip',
    price: 2,
    group: 'Sauces',
  },
  {
    id: 'bkx-chilli',
    label: 'Sweet chilli dip',
    price: 2,
    group: 'Sauces',
  },
  {
    id: 'bkx-garlic',
    label: 'Garlic mayo dip',
    price: 2,
    group: 'Sauces',
  },
]

/** A menu item as the shopper sees it, plus which department it lives in. */
export interface BrandMenuItem extends FoodListing {
  category: string
  /** Marked on the card so a first-time visitor knows where to start. */
  popular?: boolean
}

export interface BrandStore {
  id: string
  name: string
  kind: string
  logoLetter: string
  colour: string
  rating: string
  reviews: string
  branch: string
  distance: string
  deliveryTime: string
  deliveryFee: string
  minOrder: string
  hours: string
  hero: string
  categories: string[]
  items: BrandMenuItem[]
}

const dish = (d: Dish): Dish => d

export const burgerKing: BrandStore = {
  id: 'burger-king',
  name: 'Burger King',
  kind: 'Quick-service restaurant',
  logoLetter: 'BK',
  colour: '#D62300',
  rating: '4.5',
  reviews: '12.4k',
  branch: 'Al Barsha · Mall of the Emirates',
  distance: '2.1 km away',
  deliveryTime: '25–35 min',
  deliveryFee: 'AED 7 delivery',
  minOrder: 'No minimum',
  hours: 'Open until 2:00 AM',
  hero: IMG.storefront,
  categories: [
    'Burgers',
    'Chicken',
    'Plant-Based',
    'Meals',
    'Sides',
    'Drinks',
    'Desserts',
    'Breakfast',
    'Kids',
    'Sharing',
  ],
  items: [
    /* ── Burgers ─────────────────────────────────────────────────────── */
    {
      id: 'bk-whopper',
      category: 'Burgers',
      popular: true,
      title: 'Whopper',
      price: 'AED 24',
      location: 'Burger King · Al Barsha',
      images: [IMG.whopper, IMG.whopperAlt],
      specs: [],
      matchScore: 0,
      sellerType: 'Verified Business',
      dish: dish({
        prepTime: '6 min',
        allergens: ['Gluten', 'Egg', 'Sesame'],
        calories: '657 kcal',
        sizes: [
          {
            id: 'single',
            label: 'Single',
            price: 24,
            note: 'Most ordered',
          },
          {
            id: 'double',
            label: 'Double',
            price: 32,
          },
          {
            id: 'triple',
            label: 'Triple',
            price: 39,
          },
        ],
        included: [
          {
            id: 'bun',
            label: 'Sesame seed bun',
            removable: false,
          },
          {
            id: 'patty',
            label: 'Flame-grilled beef patty',
            removable: false,
          },
          {
            id: 'tomato',
            label: 'Tomatoes',
            removable: true,
          },
          {
            id: 'lettuce',
            label: 'Lettuce',
            removable: true,
          },
          {
            id: 'mayo',
            label: 'Mayo',
            removable: true,
          },
          {
            id: 'pickles',
            label: 'Pickles',
            removable: true,
          },
          {
            id: 'onion',
            label: 'Sliced onion',
            removable: true,
          },
        ],
        extras: [
          {
            id: 'bkx-cheese',
            label: 'Extra cheese',
            price: 3,
            group: 'Add to your burger',
          },
          {
            id: 'bkx-bacon',
            label: 'Beef bacon',
            price: 5,
            group: 'Add to your burger',
          },
          {
            id: 'bkx-jala',
            label: 'Jalapeños',
            price: 2,
            group: 'Add to your burger',
          },
          ...MEAL_EXTRAS,
        ],
      }),
    },
    {
      id: 'bk-double-cheese',
      category: 'Burgers',
      title: 'Double Cheeseburger',
      price: 'AED 19',
      location: 'Burger King · Al Barsha',
      images: [IMG.cheeseburger, IMG.whopperAlt],
      specs: [],
      matchScore: 0,
      sellerType: 'Verified Business',
      dish: dish({
        prepTime: '5 min',
        allergens: ['Gluten', 'Dairy', 'Mustard'],
        calories: '437 kcal',
        sizes: [
          {
            id: 'std',
            label: 'Regular',
            price: 19,
          },
          {
            id: 'triple',
            label: 'Triple patty',
            price: 26,
          },
        ],
        included: [
          {
            id: 'bun',
            label: 'Soft bun',
            removable: false,
          },
          {
            id: 'patties',
            label: 'Two beef patties',
            removable: false,
          },
          {
            id: 'cheese',
            label: 'American cheese',
            removable: true,
          },
          {
            id: 'pickles',
            label: 'Pickles',
            removable: true,
          },
          {
            id: 'ketchup',
            label: 'Ketchup',
            removable: true,
          },
          {
            id: 'mustard',
            label: 'Mustard',
            removable: true,
          },
        ],
        extras: [
          {
            id: 'bkx-patty',
            label: 'Extra patty',
            price: 7,
            group: 'Add to your burger',
          },
          ...MEAL_EXTRAS,
        ],
      }),
    },
    {
      id: 'bk-angus',
      category: 'Burgers',
      title: 'Steakhouse Angus',
      price: 'AED 36',
      location: 'Burger King · Al Barsha',
      images: [IMG.angus],
      specs: [],
      matchScore: 0,
      sellerType: 'Verified Business',
      dish: dish({
        prepTime: '8 min',
        allergens: ['Gluten', 'Dairy', 'Egg'],
        calories: '790 kcal',
        sizes: [
          {
            id: 'std',
            label: 'Single Angus',
            price: 36,
          },
          {
            id: 'dbl',
            label: 'Double Angus',
            price: 48,
          },
        ],
        included: [
          {
            id: 'brioche',
            label: 'Brioche bun',
            removable: false,
          },
          {
            id: 'angus',
            label: 'Angus beef patty',
            removable: false,
          },
          {
            id: 'cheddar',
            label: 'Smoked cheddar',
            removable: true,
          },
          {
            id: 'crispy',
            label: 'Crispy onions',
            removable: true,
          },
          {
            id: 'bacon',
            label: 'Beef bacon',
            removable: true,
          },
          {
            id: 'steaksauce',
            label: 'Steakhouse sauce',
            removable: true,
          },
        ],
        extras: [...MEAL_EXTRAS],
      }),
    },
    /* ── Chicken ─────────────────────────────────────────────────────── */
    {
      id: 'bk-royale',
      category: 'Chicken',
      popular: true,
      title: 'Crispy Chicken Royale',
      price: 'AED 22',
      location: 'Burger King · Al Barsha',
      images: [IMG.chicken, IMG.nuggets],
      specs: [],
      matchScore: 0,
      sellerType: 'Verified Business',
      dish: dish({
        prepTime: '7 min',
        allergens: ['Gluten', 'Egg', 'Sesame'],
        calories: '590 kcal',
        spiceLevel: 'Mild',
        sizes: [
          {
            id: 'orig',
            label: 'Original',
            price: 22,
          },
          {
            id: 'spicy',
            label: 'Spicy',
            price: 22,
            note: 'Most ordered',
          },
          {
            id: 'zinger',
            label: 'Zinger',
            price: 24,
          },
        ],
        included: [
          {
            id: 'bun',
            label: 'Long sesame bun',
            removable: false,
          },
          {
            id: 'fillet',
            label: 'Crispy chicken fillet',
            removable: false,
          },
          {
            id: 'lettuce',
            label: 'Lettuce',
            removable: true,
          },
          {
            id: 'mayo',
            label: 'Creamy mayo',
            removable: true,
          },
        ],
        extras: [
          {
            id: 'bkx-cheese2',
            label: 'Cheese slice',
            price: 3,
            group: 'Add to your sandwich',
          },
          ...MEAL_EXTRAS,
        ],
      }),
    },
    {
      id: 'bk-nuggets',
      category: 'Chicken',
      title: 'Chicken Nuggets',
      price: 'AED 13',
      location: 'Burger King · Al Barsha',
      images: [IMG.nuggets],
      specs: [],
      matchScore: 0,
      sellerType: 'Verified Business',
      dish: dish({
        prepTime: '5 min',
        allergens: ['Gluten'],
        calories: '395 kcal',
        sizes: [
          {
            id: '6',
            label: '6 pieces',
            price: 13,
          },
          {
            id: '9',
            label: '9 pieces',
            price: 17,
            note: 'Most ordered',
          },
          {
            id: '20',
            label: '20 pieces',
            price: 33,
          },
        ],
        included: [
          {
            id: 'nuggets',
            label: 'Breaded chicken nuggets',
            removable: false,
          },
        ],
        extras: [...SAUCES, ...MEAL_EXTRAS],
      }),
    },
    /* ── Plant-Based ─────────────────────────────────────────────────── */
    {
      id: 'bk-plant-whopper',
      category: 'Plant-Based',
      title: 'Plant-Based Whopper',
      price: 'AED 27',
      location: 'Burger King · Al Barsha',
      images: [IMG.plant],
      specs: [],
      matchScore: 0,
      sellerType: 'Verified Business',
      dish: dish({
        prepTime: '7 min',
        allergens: ['Gluten', 'Soy', 'Egg', 'Sesame'],
        calories: '610 kcal',
        sizes: [
          {
            id: 'single',
            label: 'Single',
            price: 27,
          },
          {
            id: 'double',
            label: 'Double',
            price: 36,
          },
        ],
        included: [
          {
            id: 'bun',
            label: 'Sesame seed bun',
            removable: false,
          },
          {
            id: 'patty',
            label: 'Plant-based patty',
            removable: false,
          },
          {
            id: 'tomato',
            label: 'Tomatoes',
            removable: true,
          },
          {
            id: 'lettuce',
            label: 'Lettuce',
            removable: true,
          },
          {
            id: 'mayo',
            label: 'Mayo',
            removable: true,
          },
          {
            id: 'pickles',
            label: 'Pickles',
            removable: true,
          },
          {
            id: 'onion',
            label: 'Sliced onion',
            removable: true,
          },
        ],
        extras: [...MEAL_EXTRAS],
      }),
    },
    /* ── Meals ───────────────────────────────────────────────────────── */
    {
      id: 'bk-whopper-meal',
      category: 'Meals',
      popular: true,
      title: 'Whopper Meal',
      price: 'AED 34',
      location: 'Burger King · Al Barsha',
      images: [IMG.bundle],
      specs: [],
      matchScore: 0,
      sellerType: 'Verified Business',
      dish: dish({
        prepTime: '7 min',
        allergens: ['Gluten', 'Egg', 'Sesame'],
        calories: '1,120 kcal',
        sizes: [
          {
            id: 'med',
            label: 'Medium meal',
            price: 34,
            note: 'Most ordered',
          },
          {
            id: 'large',
            label: 'Large meal',
            price: 39,
          },
        ],
        included: [
          {
            id: 'whopper',
            label: 'Whopper',
            removable: false,
          },
          {
            id: 'fries',
            label: 'Fries',
            removable: true,
          },
          {
            id: 'drink',
            label: 'Soft drink',
            removable: true,
          },
        ],
        extras: [
          {
            id: 'bkx-swap-rings',
            label: 'Swap fries for onion rings',
            price: 3,
            group: 'Change your side',
          },
          {
            id: 'bkx-swap-cheesy',
            label: 'Swap fries for cheesy fries',
            price: 5,
            group: 'Change your side',
          },
          {
            id: 'bkx-sundae',
            label: 'Add an Oreo sundae',
            price: 12,
            group: 'Finish with dessert',
          },
        ],
      }),
    },
    /* ── Sides ───────────────────────────────────────────────────────── */
    {
      id: 'bk-fries',
      category: 'Sides',
      popular: true,
      title: 'French Fries',
      price: 'AED 7',
      location: 'Burger King · Al Barsha',
      images: [IMG.fries, IMG.onionRings],
      specs: [],
      matchScore: 0,
      sellerType: 'Verified Business',
      dish: dish({
        prepTime: '3 min',
        allergens: [],
        calories: '312 kcal',
        sizes: [
          {
            id: 's',
            label: 'Small',
            price: 7,
          },
          {
            id: 'm',
            label: 'Medium',
            price: 9,
            note: 'Most ordered',
          },
          {
            id: 'l',
            label: 'Large',
            price: 12,
          },
        ],
        included: [
          {
            id: 'fries',
            label: 'Salted fries',
            removable: false,
          },
          {
            id: 'salt',
            label: 'Salt',
            removable: true,
          },
        ],
        extras: [
          {
            id: 'bkx-cheesesauce',
            label: 'Cheese sauce',
            price: 5,
            group: 'Load them up',
          },
          ...SAUCES,
        ],
      }),
    },
    {
      id: 'bk-rings',
      category: 'Sides',
      title: 'Onion Rings',
      price: 'AED 11',
      location: 'Burger King · Al Barsha',
      images: [IMG.onionRings],
      specs: [],
      matchScore: 0,
      sellerType: 'Verified Business',
      dish: dish({
        prepTime: '4 min',
        allergens: ['Gluten'],
        sizes: [
          {
            id: 'm',
            label: 'Medium · 8 pieces',
            price: 11,
          },
          {
            id: 'l',
            label: 'Large · 12 pieces',
            price: 14,
          },
        ],
        included: [
          {
            id: 'rings',
            label: 'Battered onion rings',
            removable: false,
          },
        ],
        extras: [...SAUCES],
      }),
    },
    /* ── Drinks ──────────────────────────────────────────────────────── */
    {
      id: 'bk-pepsi',
      category: 'Drinks',
      title: 'Pepsi',
      price: 'AED 6',
      location: 'Burger King · Al Barsha',
      images: [IMG.drink],
      specs: [],
      matchScore: 0,
      sellerType: 'Verified Business',
      dish: dish({
        prepTime: '1 min',
        allergens: [],
        sizes: [
          {
            id: 's',
            label: 'Small · 300 ml',
            price: 6,
          },
          {
            id: 'm',
            label: 'Medium · 400 ml',
            price: 8,
          },
          {
            id: 'l',
            label: 'Large · 500 ml',
            price: 10,
          },
        ],
        included: [
          {
            id: 'pepsi',
            label: 'Pepsi',
            removable: false,
          },
          {
            id: 'ice',
            label: 'Ice',
            removable: true,
          },
        ],
        extras: [],
      }),
    },
    /* ── Desserts ────────────────────────────────────────────────────── */
    {
      id: 'bk-sundae',
      category: 'Desserts',
      title: 'Oreo Sundae',
      price: 'AED 12',
      location: 'Burger King · Al Barsha',
      images: [IMG.sundae],
      specs: [],
      matchScore: 0,
      sellerType: 'Verified Business',
      dish: dish({
        prepTime: '2 min',
        allergens: ['Dairy', 'Gluten'],
        calories: '289 kcal',
        sizes: [
          {
            id: 'std',
            label: 'Regular',
            price: 12,
          },
        ],
        included: [
          {
            id: 'icecream',
            label: 'Vanilla soft serve',
            removable: false,
          },
          {
            id: 'oreo',
            label: 'Crushed Oreo',
            removable: true,
          },
          {
            id: 'sauce',
            label: 'Chocolate sauce',
            removable: true,
          },
        ],
        extras: [
          {
            id: 'bkx-caramel',
            label: 'Caramel sauce instead',
            price: 0,
            group: 'Change the sauce',
          },
          {
            id: 'bkx-straw',
            label: 'Strawberry sauce instead',
            price: 0,
            group: 'Change the sauce',
          },
        ],
      }),
    },
    {
      id: 'bk-shake',
      category: 'Desserts',
      title: 'Chocolate Milkshake',
      price: 'AED 16',
      location: 'Burger King · Al Barsha',
      images: [IMG.shake],
      specs: [],
      matchScore: 0,
      sellerType: 'Verified Business',
      dish: dish({
        prepTime: '3 min',
        allergens: ['Dairy'],
        sizes: [
          {
            id: 'm',
            label: 'Medium · 400 ml',
            price: 16,
          },
          {
            id: 'l',
            label: 'Large · 550 ml',
            price: 20,
          },
        ],
        included: [
          {
            id: 'shake',
            label: 'Chocolate shake',
            removable: false,
          },
          {
            id: 'cream',
            label: 'Whipped cream',
            removable: true,
          },
        ],
        extras: [
          {
            id: 'bkx-oreo-shake',
            label: 'Add Oreo pieces',
            price: 3,
            group: 'Add to your shake',
          },
        ],
      }),
    },
    /* ── Breakfast ───────────────────────────────────────────────────── */
    {
      id: 'bk-muffin',
      category: 'Breakfast',
      title: 'Egg & Cheese Muffin',
      price: 'AED 13',
      location: 'Burger King · Al Barsha',
      images: [IMG.breakfast],
      specs: [],
      matchScore: 0,
      sellerType: 'Verified Business',
      dish: dish({
        prepTime: '4 min',
        allergens: ['Gluten', 'Egg', 'Dairy'],
        sizes: [
          {
            id: 'std',
            label: 'Regular',
            price: 13,
          },
        ],
        included: [
          {
            id: 'muffin',
            label: 'Toasted English muffin',
            removable: false,
          },
          {
            id: 'egg',
            label: 'Folded egg',
            removable: false,
          },
          {
            id: 'cheese',
            label: 'American cheese',
            removable: true,
          },
        ],
        extras: [
          {
            id: 'bkx-bfbacon',
            label: 'Beef bacon',
            price: 5,
            group: 'Add to your muffin',
          },
          {
            id: 'bkx-sausage',
            label: 'Chicken sausage',
            price: 5,
            group: 'Add to your muffin',
          },
          {
            id: 'bkx-coffee',
            label: 'Regular coffee',
            price: 9,
            group: 'Make it a breakfast set',
          },
        ],
      }),
    },
    /* ── Kids ────────────────────────────────────────────────────────── */
    {
      id: 'bk-kids',
      category: 'Kids',
      title: 'Kids Nugget Meal',
      price: 'AED 21',
      location: 'Burger King · Al Barsha',
      images: [IMG.kids],
      specs: [],
      matchScore: 0,
      sellerType: 'Verified Business',
      dish: dish({
        prepTime: '5 min',
        allergens: ['Gluten'],
        calories: '480 kcal',
        sizes: [
          {
            id: 'std',
            label: 'Kids box',
            price: 21,
          },
        ],
        included: [
          {
            id: 'nuggets',
            label: '4 chicken nuggets',
            removable: false,
          },
          {
            id: 'fries',
            label: 'Small fries',
            removable: true,
          },
          {
            id: 'toy',
            label: 'Toy',
            removable: true,
          },
        ],
        extras: [
          {
            id: 'bkx-juice',
            label: 'Apple juice',
            price: 0,
            group: 'Choose a drink',
          },
          {
            id: 'bkx-milk',
            label: 'Milk',
            price: 0,
            group: 'Choose a drink',
          },
          {
            id: 'bkx-water',
            label: 'Water',
            price: 0,
            group: 'Choose a drink',
          },
        ],
      }),
    },
    /* ── Sharing ─────────────────────────────────────────────────────── */
    {
      id: 'bk-family',
      category: 'Sharing',
      popular: true,
      title: 'Family Bundle · Feeds 4',
      price: 'AED 119',
      location: 'Burger King · Al Barsha',
      images: [IMG.bundle],
      specs: [],
      matchScore: 0,
      sellerType: 'Verified Business',
      dish: dish({
        prepTime: '12 min',
        allergens: ['Gluten', 'Egg', 'Dairy', 'Sesame'],
        sizes: [
          {
            id: 'std',
            label: 'Feeds 4',
            price: 119,
          },
          {
            id: 'big',
            label: 'Feeds 6',
            price: 169,
          },
        ],
        included: [
          {
            id: 'whoppers',
            label: '2 Whoppers',
            removable: false,
          },
          {
            id: 'royales',
            label: '2 Chicken Royales',
            removable: false,
          },
          {
            id: 'fries',
            label: '4 medium fries',
            removable: true,
          },
          {
            id: 'nuggets',
            label: '9 nuggets',
            removable: true,
          },
          {
            id: 'drinks',
            label: '4 soft drinks',
            removable: true,
          },
        ],
        extras: [
          {
            id: 'bkx-all-large',
            label: 'Upgrade all fries to large',
            price: 12,
            group: 'Upgrade',
          },
          {
            id: 'bkx-4-sundaes',
            label: 'Add 4 sundaes',
            price: 40,
            group: 'Upgrade',
          },
        ],
      }),
    },
  ],
}

/** Brands the shopper can reach by name. One for now; the shape allows more. */
export const brandStores: BrandStore[] = [burgerKing]

export const findBrand = (text: string): BrandStore | undefined => {
  const t = text.toLowerCase()
  if (
    t.includes('burger king') ||
    t.includes('burgerking') ||
    /\bbk\b/.test(t)
  )
    return burgerKing
  return undefined
}
